// The dynamic branding system. Eight operator-chosen colors are the single source of
// truth for every surface, hairline, label and control in the app.
//
// How it reaches the DOM: `applyBranding` writes them as `--brand-*` custom properties
// on <html>. src/index.css maps its Tailwind @theme tokens onto those properties, so a
// class written months ago (`bg-n-surface`, `text-paper-ink`, `border-brand-border`)
// re-paints the instant a color changes -- no component subscribes to the palette and
// no page needed a per-color rewrite.
//
// Dark mode is DERIVED, not chosen: a background darker than mid-grey gets `.dark` on
// <html>. Keeping a separate light/dark switch alongside a free-form background picker
// would have given the app two disagreeing opinions about whether it is dark.

export interface BrandingConfig {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  button: string;
}

export type BrandingKey = keyof BrandingConfig;

/** Ships as the product default. Surface is white rather than the background's #F6F6F2
 *  so a card still reads as a card -- an identical surface and background collapses the
 *  whole tray/panel hierarchy into one flat sheet. Change it in Settings if you want it. */
export const DEFAULT_BRANDING: BrandingConfig = {
  primary: "#388087",
  secondary: "#6FB3B8",
  background: "#F6F6F2",
  surface: "#FFFFFF",
  border: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  button: "#388087",
};

/** The dark counterpart reached by the palette toggle (command palette, Settings). */
export const DEFAULT_DARK_BRANDING: BrandingConfig = {
  primary: "#5EA8AF",
  secondary: "#8ECBD0",
  background: "#0B1220",
  surface: "#131C2C",
  border: "#243349",
  textPrimary: "#E8EDF5",
  textSecondary: "#93A2B8",
  button: "#5EA8AF",
};

interface BrandingField {
  key: BrandingKey;
  label: string;
  /** The custom property this key writes. Shown in the UI so the mapping is not folklore. */
  cssVar: string;
  helper: string;
}

// Ordered as the Branding panel renders them: brand hues first, then surfaces, then type.
export const BRANDING_FIELDS: readonly BrandingField[] = [
  { key: "primary", label: "Primary", cssVar: "--brand-primary", helper: "Accents, active nav, focus rings" },
  { key: "secondary", label: "Secondary", cssVar: "--brand-secondary", helper: "Supporting highlights and charts" },
  { key: "background", label: "Background", cssVar: "--brand-bg", helper: "The page canvas behind everything" },
  { key: "surface", label: "Surface / Card", cssVar: "--brand-surface", helper: "Cards, panels, sidebars" },
  { key: "border", label: "Border", cssVar: "--brand-border", helper: "Every hairline and divider" },
  { key: "textPrimary", label: "Text primary", cssVar: "--brand-text-primary", helper: "Headings and body copy" },
  { key: "textSecondary", label: "Text secondary", cssVar: "--brand-text-secondary", helper: "Labels, meta, captions" },
  { key: "button", label: "Button", cssVar: "--brand-button", helper: "Primary action fill" },
];

export interface BrandingPreset {
  name: string;
  config: BrandingConfig;
}

export const BRANDING_PRESETS: readonly BrandingPreset[] = [
  { name: "Teal Fresh", config: DEFAULT_BRANDING },
  {
    name: "Ocean Breeze",
    config: {
      primary: "#0E7490",
      secondary: "#38BDF8",
      background: "#F1F7FB",
      surface: "#FFFFFF",
      border: "#DCE7F0",
      textPrimary: "#0B2540",
      textSecondary: "#5A7189",
      button: "#0E7490",
    },
  },
  {
    name: "Indigo SaaS",
    config: {
      primary: "#4F46E5",
      secondary: "#818CF8",
      background: "#F8FAFC",
      surface: "#FFFFFF",
      border: "#E2E8F0",
      textPrimary: "#0F172A",
      textSecondary: "#64748B",
      button: "#4F46E5",
    },
  },
  {
    name: "Nordic Slate",
    config: {
      primary: "#475569",
      secondary: "#94A3B8",
      background: "#F4F5F7",
      surface: "#FFFFFF",
      border: "#DFE3E8",
      textPrimary: "#111827",
      textSecondary: "#6B7280",
      button: "#334155",
    },
  },
  { name: "Dark Slate", config: DEFAULT_DARK_BRANDING },
  {
    name: "Midnight Purple",
    config: {
      primary: "#A972DB",
      secondary: "#C6A3EC",
      background: "#0F0B16",
      surface: "#1A1424",
      border: "#2C2440",
      textPrimary: "#EFEAF5",
      textSecondary: "#A79FB5",
      button: "#A972DB",
    },
  },
];

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Accepts `fff`, `#fff`, `FFFFFF`, `#ffffff`; returns canonical `#rrggbb`, or null if
 *  the text is not yet a color. Callers keep the raw text on null so a half-typed hex
 *  is not erased mid-keystroke. */
export function normalizeHex(input: string): string | null {
  const trimmed = input.trim();
  if (!HEX_RE.test(trimmed)) return null;
  const body = trimmed.replace("#", "").toLowerCase();
  const full = body.length === 3 ? body.replace(/./g, (c) => c + c) : body;
  return `#${full}`;
}

function channels(hex: string): [number, number, number] {
  const n = normalizeHex(hex) ?? "#000000";
  return [
    parseInt(n.slice(1, 3), 16),
    parseInt(n.slice(3, 5), 16),
    parseInt(n.slice(5, 7), 16),
  ];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const INK_DARK = "#0F172A";
const INK_LIGHT = "#FFFFFF";

/** WCAG contrast ratio between two colors, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Readable ink for a filled surface. CSS has no portable `contrast-color()` yet, so a
 *  pale Button color still needs dark text and this is where that gets decided.
 *
 *  It compares BOTH candidates rather than thresholding luminance at 0.5. A mid-tone
 *  like the default secondary (#6FB3B8, luminance 0.40) falls on the dark side of that
 *  threshold and would take white text at 2.3:1 -- unreadable. Dark ink on the same
 *  color is 7.1:1. The crossover is nearer 0.21 than 0.5, and picking the higher of the
 *  two real ratios means there is no threshold to get wrong. */
export function readableInk(hex: string): string {
  return contrastRatio(hex, INK_DARK) >= contrastRatio(hex, INK_LIGHT) ? INK_DARK : INK_LIGHT;
}

export function isDarkBranding(config: BrandingConfig): boolean {
  return luminance(config.background) < 0.5;
}

/** Custom-property name for each config key, and the derived properties that follow
 *  from them. Kept as data because the pre-paint script in index.html writes the same
 *  set before React loads -- the two must not drift. */
export const BRANDING_VARS: Readonly<Record<BrandingKey, string>> = {
  primary: "--brand-primary",
  secondary: "--brand-secondary",
  background: "--brand-bg",
  surface: "--brand-surface",
  border: "--brand-border",
  textPrimary: "--brand-text-primary",
  textSecondary: "--brand-text-secondary",
  button: "--brand-button",
};

/** Writes the palette to an element's inline style (always <html> in practice) and
 *  syncs the derived `.dark` class. Inline style beats the stylesheet's :root defaults. */
export function applyBranding(el: HTMLElement, config: BrandingConfig): void {
  for (const key of Object.keys(BRANDING_VARS) as BrandingKey[]) {
    el.style.setProperty(BRANDING_VARS[key], config[key]);
  }
  el.style.setProperty("--brand-on-primary", readableInk(config.primary));
  el.style.setProperty("--brand-on-button", readableInk(config.button));
  el.classList.toggle("dark", isDarkBranding(config));
}

// --- tenant branding + each user's own light/dark choice ------------------------------------
//
// Two inputs, never merged into one: the TENANT decides the brand (a light palette, set on the
// Branding page when their platform admin allows it), and each USER decides light or dark for
// themselves. The palette actually painted is computed from both, so a white-labelled workspace
// still gives every member dark mode -- in the brand's own accent colours.

export type ColorMode = "light" | "dark";

/** Mix a colour toward white. `amount` 0 leaves it, 1 makes it white. */
function mixWithWhite(hex: string, amount: number): string {
  const [r, g, b] = channels(hex).map((c) => Math.round(c + (255 - c) * amount));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/** The colour, lightened only as far as it takes to read against `against`.
 *
 *  3:1 is WCAG's minimum for UI components and large text, which is what an accent is used
 *  for. A brand navy on a dark surface fails that and would vanish; this lifts it in small steps
 *  and stops at the first shade that passes, so the brand stays as close to itself as it can. */
export function ensureContrast(hex: string, against: string, minRatio = 3): string {
  let out = normalizeHex(hex) ?? hex;
  for (let step = 0; step <= 20 && contrastRatio(out, against) < minRatio; step += 1) {
    out = mixWithWhite(hex, step / 20);
  }
  return out;
}

/** A dark palette that keeps the brand: ORAI's dark neutrals with the tenant's own accents. */
export function deriveDarkBranding(base: BrandingConfig): BrandingConfig {
  const surface = DEFAULT_DARK_BRANDING.surface;
  return {
    ...DEFAULT_DARK_BRANDING,
    primary: ensureContrast(base.primary, surface),
    secondary: ensureContrast(base.secondary, surface),
    button: ensureContrast(base.button, surface),
  };
}

/** The palette to paint: the tenant's, or ORAI's when they have none, in the user's mode.
 *
 *  With no tenant palette the two ORAI palettes are returned exactly as designed, not derived,
 *  so a workspace that never white-labels looks precisely as it did before this existed. */
export function composeBranding(tenant: BrandingConfig | null, mode: ColorMode): BrandingConfig {
  if (!tenant) return mode === "dark" ? DEFAULT_DARK_BRANDING : DEFAULT_BRANDING;
  return mode === "dark" ? deriveDarkBranding(tenant) : tenant;
}
